import React, { useEffect, useMemo, useRef, useState } from 'react';
import theme from './ProjectRooms.module.scss';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { LoadingIndicator, Modal, ModalHeader, ModalBody, ModalFooter, Select } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { useAsyncState, useGetProjectId, useIsMobile } from '@/utils/hooks';
import { OptionalId, RoomDto } from '@/types';
import { RoomList } from './RoomList';
import { RoomInfo } from './RoomInfo/RoomInfo';
import { arrayMove } from '@ygtechnologies/bygr-frontend-component-library';
import { copyRoomWithOptionGroups, createRoom, deleteRoom, getRooms, reorderRooms, RoomRequest, updateRoom } from '@/services/rooms';
import { useQueryClient } from '@tanstack/react-query';
import { ReorderPopupBar } from '@/components/ReorderPopupBar/ReorderPopupBar';
import RouterPrompt from '@/components/RouterPrompt';
import { Button } from '@/components/Buttons';
import { Button as RubusButton, NoItemsCard } from '@ygtechnologies/rubus-ui';
import { faPlus } from '@fortawesome/pro-light-svg-icons';

export interface ProjectRoomsProps { }

export const ProjectRooms: React.FC<ProjectRoomsProps> = ({ }) => {
    const projectId = useGetProjectId();
    const queryClient = useQueryClient();

    const [databaseTags, setDatabaseTags] = useAsyncState(() => getRooms(projectId!), [projectId], null);
    const [reorderedTags, setReorderedTags] = useState<RoomDto[] | null>(null);
    const [activeRoom, setActiveRoom] = useState<OptionalId<RoomDto> | null>(null);
    const [editValue, setEditValue] = useState<OptionalId<RoomDto> | null>(null);
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const listEndRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const newTags = arrayMove(reorderedTags ?? databaseTags!, oldIndex, newIndex).map((tags, index) => ({
            ...tags!,
            sortOrder: index,
        }));
        setReorderedTags(newTags);
    };

    const cancelReorder = () => setReorderedTags(null);

    const acceptReorder = async () => {
        if (!projectId || !reorderedTags) return;

        await reorderRooms(
            projectId,
            reorderedTags.map((x) => x.id)
        );

        setDatabaseTags(reorderedTags);
        setReorderedTags(null);
    };

    const createNewRoom = () => {
        const newRoom: OptionalId<RoomDto> = {
            sortOrder: Math.max(...databaseTags!.map((x) => x.sortOrder), 0) + 1,
            name: '',
            labelNote: null,
            sellerDescription: '',
            buyerDescription: '',
            icon: 'bed',
        };
        setActiveRoom(newRoom);
        setEditValue(newRoom);

        setTimeout(() => {
            listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
    };

    const confirmOrExecute = (action: () => void) => {
        if (editValue) {
            setPendingAction(() => action);
            setShowUnsavedChangesDialog(true);
        } else {
            action();
        }
    };

    const handleNewClick = () => {
        confirmOrExecute(createNewRoom);
    };

    const handleTagClick = (tag: RoomDto) => {
        confirmOrExecute(() => {
            setActiveRoom(tag);
            setEditValue(null);
        });
    };

    const displayTags = useMemo(() => {
        const baseTags = reorderedTags ?? databaseTags ?? [];

        if (editValue && !editValue.id) {
            return [...baseTags, editValue as RoomDto];
        }

        if (editValue && editValue.id) {
            return baseTags.map(tag => tag.id === editValue.id ? editValue as RoomDto : tag);
        }

        return baseTags;
    }, [reorderedTags, databaseTags, editValue]);

    const handleDelete = async (value: RoomDto) => {
        if (!projectId) return;

        setActiveRoom(null);
        await deleteRoom(projectId, value.id);
        setDatabaseTags((prev) => prev!.filter((x) => x !== value));
    };

    const handleCopy = async (value: RoomDto) => {
        if (!projectId) return;

        const sortOrder = Math.max(...(databaseTags ?? []).map((x) => x.sortOrder), 0) + 1;
        const existingNames = (databaseTags ?? []).map((x) => x.name);
        const newRoom = await copyRoomWithOptionGroups(projectId, value, sortOrder, existingNames);

        await queryClient.invalidateQueries({ queryKey: ['getRooms', projectId] });
        setDatabaseTags((prev) => [...prev!, newRoom]);
        setActiveRoom(newRoom);
        setEditValue(newRoom);
    };

    const handleSave = async (value: OptionalId<RoomDto>, unitsToLink: number[], unitsToUnlink: number[]) => {
        if (!projectId) return;

        const request = {
            ...value,
            unitsToLink,
            unitsToUnlink,
        } satisfies RoomRequest;

        if (value.id) {
            const newValue = await updateRoom(projectId, value.id, request);
            const oldItem = databaseTags?.find((x) => x.id === value.id);
            if (oldItem) {
                setDatabaseTags((prev) => arrayReplace(prev!, oldItem, newValue));
                setActiveRoom(newValue);
            }
        } else {
            const newValue = await createRoom(projectId, request);
            setDatabaseTags((prev) => [...prev!, newValue]);
            setActiveRoom(newValue);
        }

        setEditValue(null);
        return Promise.resolve();
    };

    const handleSaveAndCreateNew = async (value: OptionalId<RoomDto>, unitsToLink: number[], unitsToUnlink: number[]) => {
        if (!projectId) return;

        const request = {
            ...value,
            unitsToLink,
            unitsToUnlink,
        } satisfies RoomRequest;

        if (value.id) {
            const newValue = await updateRoom(projectId, value.id, request);
            const oldItem = databaseTags?.find((x) => x.id === value.id);
            if (oldItem) {
                setDatabaseTags((prev) => arrayReplace(prev!, oldItem, newValue));
            }
        } else {
            const newValue = await createRoom(projectId, request);
            setDatabaseTags((prev) => [...prev!, newValue]);
        }

        const currentTags = databaseTags ?? [];
        const newRoom: OptionalId<RoomDto> = {
            sortOrder: Math.max(...currentTags.map((x) => x.sortOrder), 0) + 2,
            name: '',
            labelNote: null,
            sellerDescription: '',
            buyerDescription: '',
            icon: 'bed',
        };
        setActiveRoom(newRoom);
        setEditValue(newRoom);

        setTimeout(() => {
            listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);

        return Promise.resolve();
    };

    useEffect(() => {
        if (!activeRoom || (activeRoom.id && !databaseTags?.find((x) => x.id === activeRoom.id))) {
            setActiveRoom(databaseTags && databaseTags.length > 0 ? databaseTags[0] : null);
        }
    }, [databaseTags, activeRoom]);

    if (!databaseTags) {
        return <LoadingIndicator />;
    }
    return (
        <>
            <div>
                <Trans id="project.rooms.description">
                    Create, edit and remove rooms in this project.
                </Trans>
            </div>
            <div className={theme.container}>
                {isMobile ? (
                    <div className={theme.mobileSelector}>
                        <Select
                            options={displayTags.map((tag) => ({
                                label: tag.name || t`New room`,
                                value: tag.id ?? -1,
                            }))}
                            value={
                                activeRoom
                                    ? { label: activeRoom.name || t`New room`, value: activeRoom.id ?? -1 }
                                    : null
                            }
                            onChange={(opt: any) => {
                                const selected = displayTags.find((tag) => (tag.id ?? -1) === opt.value);
                                if (selected) handleTagClick(selected);
                            }}
                            placeholder={t`Select a room`}
                        />
                        <RubusButton
                            variant="primary"
                            icon={faPlus}
                            onClick={handleNewClick}
                            className={theme.mobileNewRoomButton}
                        >
                            <Trans>New room</Trans>
                        </RubusButton>
                    </div>
                ) : (
                    <RoomList
                        tags={displayTags}
                        onSortEnd={onSortEnd}
                        onNewClick={handleNewClick}
                        onDelete={handleDelete}
                        activeTagId={activeRoom?.id ?? null}
                        isNewRoomActive={!!editValue && !editValue.id}
                        listEndRef={listEndRef}
                        onTagClick={handleTagClick}
                    />
                )}
                {displayTags.length === 0 ? (
                    <div className={theme.noItemsContainer}>
                        <NoItemsCard title={<Trans>There are no rooms!</Trans>} description={<Trans>Want to create a room?</Trans>} buttonProps={{ onClick: handleNewClick, children: <Trans>+ Create new room</Trans> }} />
                    </div>
                ) : (
                    activeRoom && (
                        <div className={theme.detailPane}>
                            <RoomInfo
                                room={activeRoom}
                                onSave={handleSave}
                                onSaveAndCreateNew={handleSaveAndCreateNew}
                                onDelete={handleDelete}
                                onCopy={handleCopy}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                allRooms={databaseTags}
                                onCancelNewRoom={() => {
                                    if (databaseTags && databaseTags.length > 0) {
                                        setActiveRoom(databaseTags[0]);
                                    }
                                }}
                            />
                        </div>
                    )
                )}
            </div>
            <ReorderPopupBar open={!!reorderedTags} onCancel={cancelReorder} onAccept={acceptReorder} />

            <RouterPrompt when={!!editValue} />

            <Modal
                isOpen={showUnsavedChangesDialog}
                toggle={() => {
                    setShowUnsavedChangesDialog(false);
                    setPendingAction(null);
                }}
            >
                <ModalHeader><Trans>Unsaved changes</Trans></ModalHeader>
                <ModalBody>
                    <Trans>You have unsaved changes. What would you like to do?</Trans>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="tertiary"
                        onClick={() => {
                            setShowUnsavedChangesDialog(false);
                            setPendingAction(null);
                        }}
                    >
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => {
                            setShowUnsavedChangesDialog(false);
                            setEditValue(null);
                            pendingAction?.();
                            setPendingAction(null);
                        }}
                    >
                        <Trans>Discard</Trans>
                    </Button>
                    <Button
                        color="primary"
                        onClick={async () => {
                            if (editValue) {
                                await handleSave(editValue, [], []);
                            }
                            setShowUnsavedChangesDialog(false);
                            pendingAction?.();
                            setPendingAction(null);
                        }}
                    >
                        <Trans>Save and continue</Trans>
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};
